import React from 'react';
import { BerberRugBorder } from './Sprites';
import { playSound } from '../audio';
import { LEVEL_THRESHOLDS, TierEmblem } from './TierBadge';

interface ProgressionViewProps {
  score: number;
  onBack: () => void;
  playerName?: string;
}

export const ProgressionView: React.FC<ProgressionViewProps> = ({
  score,
  onBack,
  playerName
}) => {
  const currentTierIdx = LEVEL_THRESHOLDS.reduce((acc, t, i) => score >= t.xp ? i : acc, 0);
  const currentTier = LEVEL_THRESHOLDS[currentTierIdx];
  const nextTier = LEVEL_THRESHOLDS[currentTierIdx + 1] || null;

  const progressToNext = nextTier 
    ? Math.max(0, Math.min(100, ((score - currentTier.xp) / (nextTier.xp - currentTier.xp)) * 100))
    : 100;

  const xpNeeded = nextTier ? nextTier.xp - score : 0;

  return (
    <div className="fixed inset-0 w-full h-[100dvh] bg-parchment text-stone-900 flex flex-col font-sans z-[10000] overflow-hidden select-none">
      {/* Top Berber Woven Rug Border */}
      <BerberRugBorder />

      {/* Header */}
      <header className="py-2.5 px-3 sm:px-5 bg-[#fcf8f0] border-b border-[#dfd2be] shadow-xs flex justify-between items-center w-full shrink-0 z-30">
        <button 
          type="button"
          onClick={() => {
            playSound('click');
            onBack();
          }}
          className="px-3.5 py-1.5 bg-amber-900 hover:bg-amber-800 text-amber-50 font-black text-xs uppercase tracking-wider border border-amber-700 rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
        >
          <svg className="w-3 h-3 fill-amber-50 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
            <polygon points="17,4 7,12 17,20" />
          </svg>
          <span>Retour</span>
        </button>

        <div className="text-center">
          <h1 className="font-cinzel font-black text-base sm:text-lg text-amber-950 uppercase tracking-wider drop-shadow-xs">
            Votre Progression
          </h1>
        </div>

        {/* Spacer to keep title centered */}
        <div className="w-[84px] shrink-0 pointer-events-none" aria-hidden="true" />
      </header>

      {/* Content */}
      <div className="flex-1 min-h-0 overscroll-contain overflow-y-auto overflow-x-hidden p-3 sm:p-5">
        <div className="flex flex-col gap-5 max-w-2xl mx-auto pb-6">
          
          {/* Hero Emblem Card */}
          <div className="bg-gradient-to-b from-[#fdfbf7] to-[#f4eedf] rounded-2xl p-5 sm:p-6 border-2 border-amber-900/15 shadow-xl relative overflow-hidden">
            {/* Subtle geometric Berber background glow */}
            <div className={`absolute -top-12 left-1/2 -translate-x-1/2 w-80 h-80 bg-gradient-to-b ${currentTier.accentBg} rounded-full blur-2xl pointer-events-none opacity-40`} />
            
            <div className="relative flex flex-col items-center justify-center text-center mb-5">
              {/* Tier Badge & Rang Pill without overlap */}
              <div className="flex flex-col items-center gap-2.5 sm:gap-3 mb-2">
                <TierEmblem 
                  tierId={currentTier.id} 
                  size="hero" 
                  isUnlocked={true} 
                  isCurrent={true} 
                />
                <span className="px-3.5 py-1 bg-amber-950 text-amber-300 border border-amber-400/80 rounded-full text-[11px] font-black uppercase tracking-widest shadow-md whitespace-nowrap">
                  Rang {currentTier.tierNumber} / 7
                </span>
              </div>

              <div className="pt-1">
                <div className="text-xs sm:text-sm font-black tracking-widest text-amber-700/90 font-mono mb-0.5">
                  {currentTier.tifinagh}
                </div>
                <h2 className="text-2xl sm:text-3xl font-cinzel font-black text-amber-950 tracking-wide drop-shadow-xs">
                  {currentTier.title}
                </h2>
                <p className="text-stone-700 font-bold text-sm sm:text-base mt-0.5">
                  {currentTier.desc}
                </p>
                <p className="text-xs sm:text-sm text-stone-600 italic max-w-md mx-auto mt-2 px-3 py-1.5 bg-amber-900/5 rounded-xl border border-amber-900/10 leading-relaxed">
                  « {currentTier.quote} »
                </p>
              </div>
            </div>
            
            {/* XP Progress Bar */}
            <div className="relative bg-white/85 p-3.5 sm:p-5 rounded-2xl border border-amber-900/15 shadow-inner">
              <div className="flex justify-between items-center mb-2 text-xs sm:text-sm">
                <span className="font-bold text-amber-950 uppercase tracking-wide flex items-center gap-1.5">
                  <span>⚡ Expérience :</span>
                  <span className="text-amber-700 font-black font-mono text-sm sm:text-base">{score.toLocaleString('fr-FR')} XP</span>
                </span>
                {nextTier && (
                  <span className="font-semibold text-stone-500 text-[11px] sm:text-xs">
                    Objectif : <strong className="text-stone-800 font-mono">{nextTier.xp.toLocaleString('fr-FR')} XP</strong>
                  </span>
                )}
              </div>
              
              <div className="h-5 bg-stone-200/90 rounded-full overflow-hidden p-0.5 border border-amber-900/20 shadow-inner">
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 via-amber-400 to-amber-600 rounded-full transition-all duration-1000 ease-out relative shadow-sm"
                  style={{ width: `${Math.max(4, progressToNext)}%` }}
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse" />
                </div>
              </div>
              
              {nextTier ? (
                <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-amber-900/10 text-xs text-stone-600">
                  <span className="truncate">
                    Évolution suivante : <strong className="text-amber-900 font-bold">{nextTier.title}</strong> ({nextTier.tifinagh})
                  </span>
                  <span className="text-amber-800 font-black shrink-0 font-mono">+{xpNeeded.toLocaleString('fr-FR')} XP</span>
                </div>
              ) : (
                <p className="text-xs text-center mt-3 text-amber-900 font-black">
                  👑 Tu es le grand sage gardien de tous les secrets de la langue kabyle !
                </p>
              )}
            </div>
          </div>
          
          {/* Timeline & Custom Badges List */}
          <div className="bg-[#fcf9f2] rounded-2xl p-4 sm:p-5 border border-[#dfd2be] shadow-sm">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-amber-900/10">
              <h3 className="text-xs sm:text-sm font-black text-amber-950 uppercase tracking-wider flex items-center gap-2">
                <span>🛡️</span>
                <span>Grades & Emblèmes Berbères</span>
              </h3>
              <span className="text-[11px] font-bold text-stone-500">
                {currentTierIdx + 1} sur 7 Débloqués
              </span>
            </div>

            <div className="space-y-2.5 sm:space-y-3">
              {LEVEL_THRESHOLDS.map((tier, idx) => {
                const isUnlocked = score >= tier.xp;
                const isCurrent = idx === currentTierIdx;
                return (
                  <div 
                    key={tier.id} 
                    className={`flex items-center gap-3 sm:gap-4 p-3 rounded-xl border-2 transition-all relative overflow-hidden ${
                      isCurrent 
                        ? 'bg-amber-50/95 border-amber-500 shadow-md ring-2 ring-amber-400/40' 
                        : isUnlocked 
                          ? 'bg-white/90 border-stone-300 shadow-xs' 
                          : 'bg-stone-100/60 border-stone-200 opacity-50'
                    }`}
                  >
                    {/* Emblem Graphic */}
                    <TierEmblem 
                      tierId={tier.id} 
                      size="md" 
                      isUnlocked={isUnlocked} 
                      isCurrent={isCurrent} 
                    />

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className={`text-xs sm:text-sm md:text-base font-bold ${isCurrent ? 'text-amber-950 font-black' : isUnlocked ? 'text-stone-900' : 'text-stone-400'}`}>
                          {tier.title}
                        </h4>
                        <span className="text-[10px] sm:text-[11px] text-amber-700/80 font-mono font-bold">
                          {tier.tifinagh}
                        </span>
                        {isCurrent && (
                          <span className="px-2 py-0.5 bg-amber-500 text-white rounded-full text-[9px] font-black uppercase tracking-wider shadow-xs">
                            Actuel
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] sm:text-xs text-stone-600 truncate mt-0.5">
                        {tier.desc}
                      </p>
                    </div>

                    {/* Status / XP */}
                    <div className="text-right shrink-0">
                      <div className={`text-xs sm:text-sm font-bold font-mono ${isUnlocked ? 'text-amber-800' : 'text-stone-400'}`}>
                        {tier.xp.toLocaleString('fr-FR')} XP
                      </div>
                      <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider mt-0.5">
                        {isUnlocked ? (
                          <span className="text-emerald-700">Débloqué ✨</span>
                        ) : (
                          <span className="text-stone-400">Verrouillé 🔒</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
